/**
 * E38 · the portal's side of the safety plan — the field lists the data provider asks for, the seven sections, and the
 * pure helpers the hub tab uses (rows ↔ wire entries). The rules themselves (ten entries, a contact needs a name,
 * who may edit) are the decider's; the hub only shapes what it sends.
 */
export const SECTION_IDS = [
  'warningSigns',
  'copingStrategies',
  'distractions',
  'helpContacts',
  'professionals',
  'environmentSafety',
  'reasonsForLiving',
] as const;
export type SectionId = (typeof SECTION_IDS)[number];

export const ENTRY_LIMIT = 10;

export type SectionKind = 'text' | 'contact' | 'placeOrPerson' | 'professional';
export const SECTION_KIND: Record<SectionId, SectionKind> = {
  warningSigns: 'text',
  copingStrategies: 'text',
  distractions: 'placeOrPerson',
  helpContacts: 'contact',
  professionals: 'professional',
  environmentSafety: 'text',
  reasonsForLiving: 'text',
};

export type PlanEntry = {
  text?: string | null;
  name?: string | null;
  phone?: string | null;
  kind?: string | null;
};

export type PlanSection = {
  section: SectionId;
  entries: PlanEntry[];
  performer: string | null;
  performerName: string | null;
  byClient: boolean;
  occurredAt: string | null;
};

export type PlanRow = {
  subject: string;
  planId: string;
  hasPlan: boolean;
  mayEdit: boolean;
  sections: PlanSection[];
  therapists: Array<{ id: string; name: string | null; canSee: boolean }>;
};

export type StatusRow = {
  hasPlan: boolean;
  sectionsFilled: number;
  updatedAt: string | null;
  reviewedAt: string | null;
  reviewedWith: string | null;
  reviewedWithName: string | null;
  nextReviewBy: string | null;
};

export type HistoryRow = {
  entryId: string;
  occurredAt: string;
  performer: string;
  performerName: string | null;
  byClient: boolean;
  kind: 'section' | 'review';
  section: SectionId | null;
  entries: PlanEntry[] | null;
  nextReviewBy: string | null;
  note: string | null;
};

export type OpensRow = {
  subject: string;
  thisWeek: number;
  weeks: Array<{ weekStart: string; count: number }>;
};

const ENTRY_FIELDS = ['text', 'name', 'phone', 'kind'];

export const PLAN_FIELDS = [
  'subject',
  'planId',
  'hasPlan',
  'mayEdit',
  {
    sections: [
      'section',
      { entries: ENTRY_FIELDS },
      'performer',
      'performerName',
      'byClient',
      'occurredAt',
    ],
  },
  { therapists: ['id', 'name', 'canSee'] },
];

export const STATUS_FIELDS = [
  'hasPlan',
  'sectionsFilled',
  'updatedAt',
  'reviewedAt',
  'reviewedWith',
  'reviewedWithName',
  'nextReviewBy',
];

export const HISTORY_FIELDS = [
  'entryId',
  'occurredAt',
  'performer',
  'performerName',
  'byClient',
  'kind',
  'section',
  { entries: ENTRY_FIELDS },
  'nextReviewBy',
  'note',
];

export const OPENS_FIELDS = ['subject', 'thisWeek', { weeks: ['weekStart', 'count'] }];

export const COMMAND_FIELDS = ['planId', 'recorded'];

// ── the edit rows ────────────────────────────────────────────────────────────────────────────────

export type DraftRow = {
  key: string;
  text: string;
  name: string;
  phone: string;
  kind: 'person' | 'place';
};

let seq = 0;
export const blankRow = (): DraftRow => {
  seq += 1;
  return { key: `r${seq}`, text: '', name: '', phone: '', kind: 'person' };
};

export const draftFromEntries = (
  section: SectionId,
  entries: readonly PlanEntry[],
): DraftRow[] => {
  const rows = entries.map((e): DraftRow => {
    const r = blankRow();
    return SECTION_KIND[section] === 'text'
      ? { ...r, text: e.text ?? e.name ?? '' }
      : {
          ...r,
          name: e.name ?? e.text ?? '',
          phone: e.phone ?? '',
          kind: e.kind === 'place' ? 'place' : 'person',
        };
  });
  return rows.length ? rows : [blankRow()];
};

export const entriesFromDraft = (
  section: SectionId,
  rows: readonly DraftRow[],
): PlanEntry[] =>
  rows
    .filter((r) =>
      SECTION_KIND[section] === 'text'
        ? r.text.trim() !== ''
        : r.name.trim() !== '' || r.phone.trim() !== '',
    )
    .map((r) =>
      SECTION_KIND[section] === 'text'
        ? { text: r.text.trim() }
        : {
            name: r.name.trim(),
            ...(r.phone.trim() ? { phone: r.phone.trim() } : {}),
            ...(SECTION_KIND[section] === 'placeOrPerson' ? { kind: r.kind } : {}),
          },
    );

export const entryTitle = (e: PlanEntry): string => e.text ?? e.name ?? '';
export const entryLine = (e: PlanEntry): string =>
  e.phone ? `${entryTitle(e)} · ${e.phone}` : entryTitle(e);
